import { RenderLayoutError } from "../errors";
import type { RenderContext } from "../types/render";
import type {
  CompiledElement,
  LayoutAdjustmentDiagnostic,
  TextFontStyle,
} from "../types/template";
import {
  estimateCode128Modules,
  estimateQrVersion,
  qrModulesPerSide,
} from "../utils/barcode";
import { dotsPerMm } from "../utils/units";
import { assertKnownFont } from "./fontMetrics";
import { createCharMeasure, measureTextWidthMm } from "./fontWidthData";
import { resolveTextLayout } from "./textLayout";

export interface MeasuredElement {
  element: CompiledElement;
  widthMm: number;
  heightMm: number;
  margin: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  lineCount?: number;
  textValue?: string;
  textLines?: string[];
  textFontOverride?: TextFontStyle;
  textOverflowed?: boolean;
  textEllipsized?: boolean;
  diagnostic?: LayoutAdjustmentDiagnostic;
}

function estimateCode128WidthMm(
  value: string,
  moduleWidthMm: number,
  optimizeNumeric: boolean,
): number {
  return estimateCode128Modules(value, optimizeNumeric) * moduleWidthMm;
}

function ensurePositiveDimension(value: number, context: string): number {
  if (value <= 0) {
    throw new RenderLayoutError(
      `${context} resolved to non-positive dimension (${value}).`,
    );
  }
  return value;
}

function fallbackModuleWidthMm(
  element: Extract<CompiledElement, { type: "barcode"; symbology: "code128" }>,
): number {
  if (element.widthPolicy.mode === "fixed-module") {
    return element.widthPolicy.moduleWidthMm;
  }

  return Math.max(0.1, element.widthPolicy.minModuleDots / 8);
}

export function measureElement(
  element: CompiledElement,
  resolvedValue: string | undefined,
  ctx: RenderContext,
  flow: "vertical" | "horizontal",
  crossLimitMm: number,
  textScaleCap = 1,
): MeasuredElement {
  const margin = element.marginMm;

  switch (element.type) {
    case "text": {
      const value = resolvedValue ?? "";
      assertKnownFont(element);
      const explicitLines = value.split("\\&");
      const measure = createCharMeasure(
        element.font.family,
        element.font.widthMm,
      );
      const longestLineWidth = explicitLines.reduce(
        (max, line) =>
          Math.max(
            max,
            measureTextWidthMm(
              line,
              element.font.family,
              element.font.widthMm,
              measure,
            ),
          ),
        0,
      );
      const widthFromValue = Math.max(0.2, longestLineWidth);
      const widthMm = ensurePositiveDimension(
        element.widthMm ??
          (flow === "vertical" ? crossLimitMm : widthFromValue),
        "Text width",
      );
      const textLayout = resolveTextLayout({
        element,
        value,
        widthMm,
        heightMm: element.heightMm,
        maxScale: textScaleCap,
      });
      const heightMm = ensurePositiveDimension(
        element.heightMm ?? textLayout.contentHeightMm,
        "Text height",
      );

      return {
        element,
        widthMm,
        heightMm,
        margin,
        lineCount: textLayout.lineCount,
        textValue: textLayout.lines.join("\\&"),
        textLines: textLayout.lines,
        textFontOverride: textLayout.font,
        textOverflowed: textLayout.overflowed,
        textEllipsized: textLayout.ellipsized,
        diagnostic: textLayout.diagnostic,
      };
    }

    case "barcode": {
      const value = resolvedValue ?? "";

      if (element.symbology === "qrcode") {
        const version = estimateQrVersion(
          value.length,
          element.errorCorrection,
        );
        const modules = qrModulesPerSide(version);
        const sizeDots = modules * element.magnification;
        const sizeMm = sizeDots / dotsPerMm(ctx.dpi);
        const dim = ensurePositiveDimension(
          element.widthMm ?? sizeMm,
          "QR code size",
        );

        return {
          element,
          widthMm: dim,
          heightMm: element.heightMm ?? dim,
          margin,
        };
      }

      const widthMm = ensurePositiveDimension(
        element.widthMm ??
          estimateCode128WidthMm(
            value,
            fallbackModuleWidthMm(element),
            element.optimizeNumeric,
          ),
        "Barcode width",
      );
      const heightMm = ensurePositiveDimension(
        element.heightMm ?? 12,
        "Barcode height",
      );

      return {
        element,
        widthMm,
        heightMm,
        margin,
      };
    }

    case "line": {
      const thicknessMm = element.thicknessMm;
      const widthMm =
        element.orientation === "vertical"
          ? thicknessMm
          : (element.widthMm ?? (flow === "vertical" ? crossLimitMm : 8));
      const heightMm =
        element.orientation === "vertical"
          ? (element.heightMm ?? (flow === "horizontal" ? crossLimitMm : 8))
          : thicknessMm;

      return {
        element,
        widthMm: ensurePositiveDimension(widthMm, "Line width"),
        heightMm: ensurePositiveDimension(heightMm, "Line height"),
        margin,
      };
    }

    case "box": {
      const widthMm =
        element.widthMm ?? (flow === "vertical" ? crossLimitMm : 8);
      const heightMm = element.heightMm ?? 5;

      return {
        element,
        widthMm: ensurePositiveDimension(widthMm, "Box width"),
        heightMm: ensurePositiveDimension(heightMm, "Box height"),
        margin,
      };
    }

    case "spacer": {
      const widthMm =
        element.widthMm ?? (flow === "vertical" ? crossLimitMm : 1);
      const heightMm =
        element.heightMm ?? (flow === "horizontal" ? crossLimitMm : 1);

      return {
        element,
        widthMm: ensurePositiveDimension(widthMm, "Spacer width"),
        heightMm: ensurePositiveDimension(heightMm, "Spacer height"),
        margin,
      };
    }

    default: {
      const _exhaustive: never = element;
      throw new RenderLayoutError(
        `Unsupported element type in measurement: ${String(_exhaustive)}`,
      );
    }
  }
}

export function calculateMainSize(
  flow: "vertical" | "horizontal",
  measured: MeasuredElement,
): number {
  return flow === "vertical"
    ? measured.margin.top + measured.heightMm + measured.margin.bottom
    : measured.margin.left + measured.widthMm + measured.margin.right;
}

export function calculateCrossSize(
  flow: "vertical" | "horizontal",
  measured: MeasuredElement,
): number {
  return flow === "vertical"
    ? measured.margin.left + measured.widthMm + measured.margin.right
    : measured.margin.top + measured.heightMm + measured.margin.bottom;
}
